import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Spinner } from 'reactstrap'

import { getUserAlias } from 'models/ProfileInfo/selectors'
import { EVENT_TYPE } from 'resources'

import { sendAnalyticsEvent } from 'services/analytics'
import { setReklaimProtectEmailAddress } from 'services/reklaimProtect'

import { formatEmailAddress } from 'utils/email'
import { parseToken } from 'utils/jwt'

import { ModalHeader, ModalBody } from 'components/ReklaimModal'

import EmailAddressAddForm from './EmailAddressAddForm'
import EmailAddressAddSuccess from './EmailAddressAddSuccess'
import EmailAddressAddFailure from './EmailAddressAddFailure'
import EmailAddressAddConfirmation from './EmailAddressAddConfirmation'

import { TopReklaimModal, SpinnerContainer } from './styles'

const ADD_EMAIL_SCREENS = {
  EMAIL_INPUT_FORM: 'EMAIL_INPUT_FORM',
  EMAIL_CONFIRMATION: 'EMAIL_CONFIRMATION',
  EMAIL_UPDATE_SUCCESS: 'EMAIL_UPDATE_SUCCESS',
  EMAIL_UPDATE_FAILURE: 'EMAIL_UPDATE_FAILURE',
  EMAIL_VERIFYING: 'EMAIL_VERIFYING',
}

const EmailAddressAddModal = ({
  isOpen,
  closeModal,
  openModal,
  emailAddressData,
  setEmailAddressData,
  setIsEmailAddressAddModalOpenedByUser,
  isEmailAddressAddModalOpenedByUser,
}) => {
  const history = useHistory()
  const location = useLocation()
  const userAlias = useSelector(getUserAlias)

  const [pendingEmailAddress, setPendingEmailAddress] = useState('')
  const [currentScreen, setCurrentScreen] = useState(ADD_EMAIL_SCREENS.EMAIL_INPUT_FORM)
  const [hasUnveilProfileToBeScanned, setHasUnveilProfileToBeScanned] = useState(false)
  const token = useMemo(() => {
    const query = new URLSearchParams(location.search)
    return query.get('token')
  }, [location.search])

  const setScreenToEmailInputForm = useCallback(() => setCurrentScreen(ADD_EMAIL_SCREENS.EMAIL_INPUT_FORM), [])
  const setScreenToEmailConfirmation = useCallback(() => setCurrentScreen(ADD_EMAIL_SCREENS.EMAIL_CONFIRMATION), [])
  const setScreenToEmailUpdateSuccess = useCallback(() => setCurrentScreen(ADD_EMAIL_SCREENS.EMAIL_UPDATE_SUCCESS), [])
  const setScreenToEmailUpdateFailure = useCallback(() => setCurrentScreen(ADD_EMAIL_SCREENS.EMAIL_UPDATE_FAILURE), [])
  const setScreenToEmailVerifying = useCallback(() => setCurrentScreen(ADD_EMAIL_SCREENS.EMAIL_VERIFYING), [])

  const clearTokenFromURL = useCallback(() => {
    const query = new URLSearchParams(location.search)
    query.delete('token')

    history.replace({
      pathname: location.pathname,
      search: query.toString(),
    })
  }, [history, location.pathname, location.search])

  useEffect(() => {
    if (isOpen && isEmailAddressAddModalOpenedByUser) {
      setScreenToEmailInputForm()
      setPendingEmailAddress('')
    }
  }, [isOpen, isEmailAddressAddModalOpenedByUser, setScreenToEmailInputForm, setPendingEmailAddress])

  useEffect(() => {
    const handleToken = async () => {
      if (!token) return

      openModal(true)
      setScreenToEmailVerifying()
      setIsEmailAddressAddModalOpenedByUser(false)

      let formattedEmail
      try {
        const { emailAddress } = parseToken(token)
        formattedEmail = formatEmailAddress(emailAddress)
      } catch {
        sendAnalyticsEvent(EVENT_TYPE.accountAddEmailAddressModalVerifyEmailAddressSubmitFailure, {
          emailAddress: null,
        })
        setScreenToEmailUpdateFailure()
        return
      }

      setPendingEmailAddress(formattedEmail)
      sendAnalyticsEvent(EVENT_TYPE.accountAddEmailAddressModalVerifyEmailAddressSubmit, {
        emailAddress: formattedEmail,
      })

      clearTokenFromURL()
      try {
        const result = await setReklaimProtectEmailAddress(userAlias, {
          emailAddress: formattedEmail,
        })

        if (result?.error) {
          sendAnalyticsEvent(EVENT_TYPE.accountAddEmailAddressModalVerifyEmailAddressSubmitFailure, {
            emailAddress: formattedEmail,
          })
          setScreenToEmailUpdateFailure()
        } else {
          sendAnalyticsEvent(EVENT_TYPE.accountAddEmailAddressModalVerifyEmailAddressSubmitSuccess, {
            emailAddress: formattedEmail,
          })
          setHasUnveilProfileToBeScanned(false)
          setScreenToEmailUpdateSuccess()
        }
      } catch (error) {
        sendAnalyticsEvent(EVENT_TYPE.accountAddEmailAddressModalVerifyEmailAddressSubmitFailure, {
          emailAddress: formattedEmail,
        })
        setScreenToEmailUpdateFailure()
      }
    }

    handleToken()
  }, [
    token,
    clearTokenFromURL,
    userAlias,
    openModal,
    setScreenToEmailVerifying,
    setIsEmailAddressAddModalOpenedByUser,
    setScreenToEmailConfirmation,
    setScreenToEmailUpdateSuccess,
    setScreenToEmailUpdateFailure,
  ])

  const content = useMemo(() => {
    switch (currentScreen) {
      case ADD_EMAIL_SCREENS.EMAIL_INPUT_FORM:
        return (
          <EmailAddressAddForm
            emailAddressData={emailAddressData}
            setPendingEmailAddress={setPendingEmailAddress}
            setScreenToEmailConfirmation={setScreenToEmailConfirmation}
          />
        )
      case ADD_EMAIL_SCREENS.EMAIL_CONFIRMATION:
        return (
          <EmailAddressAddConfirmation
            pendingEmailAddress={pendingEmailAddress}
            setEmailAddressData={setEmailAddressData}
            closeModal={closeModal}
          />
        )
      case ADD_EMAIL_SCREENS.EMAIL_UPDATE_SUCCESS:
        return (
          <EmailAddressAddSuccess
            closeModal={closeModal}
            hasUnveilProfileToBeScanned={hasUnveilProfileToBeScanned}
            emailAddress={pendingEmailAddress}
          />
        )
      case ADD_EMAIL_SCREENS.EMAIL_UPDATE_FAILURE:
        return (
          <EmailAddressAddFailure
            setScreenToEmailConfirmation={setScreenToEmailConfirmation}
            pendingEmailAddress={pendingEmailAddress}
          />
        )
      case ADD_EMAIL_SCREENS.EMAIL_VERIFYING:
        return (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        )
      default:
        return null
    }
  }, [
    currentScreen,
    emailAddressData,
    setEmailAddressData,
    pendingEmailAddress,
    closeModal,
    setPendingEmailAddress,
    setScreenToEmailConfirmation,
    hasUnveilProfileToBeScanned,
  ])

  return (
    <TopReklaimModal isOpen={isOpen} toggle={closeModal} backdrop="static" centered>
      <ModalHeader toggle={closeModal} />
      <ModalBody>{content}</ModalBody>
    </TopReklaimModal>
  )
}

EmailAddressAddModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  emailAddressData: PropTypes.arrayOf(
    PropTypes.shape({
      emailAddress: PropTypes.string.isRequired,
      isPrimary: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  setEmailAddressData: PropTypes.func.isRequired,
  isEmailAddressAddModalOpenedByUser: PropTypes.bool.isRequired,
  setIsEmailAddressAddModalOpenedByUser: PropTypes.func.isRequired,
}

export default EmailAddressAddModal
